import { useModalFocusObserver } from "@/app/components/ModalFocusObserver";
import { useIsUserRole } from "@/app/utils/auth";
import { CourseUserDetailsDTO } from "@tumtutor/shared/dto";
import Fuse from "fuse.js";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal, { closeModal } from "../../components/modal";
import { useToastEffect } from "../../hooks";
import { useLeaveCourseMutation } from "../../services/courseApi";
import { useGetUniversitiesQuery } from "../../services/universityApi";
import CourseCard from "./CourseCard";
import { CourseInfo } from "./components/EnrollCourseMask";

interface SearchCoursesProps {
  courses: CourseUserDetailsDTO[];
  setCourseToEnroll?: (course: CourseInfo) => void;
}

export const SEARCH_MODAL_ID = "search_modal";

function SearchCoursesModal(props: SearchCoursesProps) {
  const { t } = useTranslation(["homePage", "coursePage", "commons"]);
  const [searchQuery, setSearchQuery] = useState("");
  const modalRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [filteredCourses, setFilteredCourses] = useState<
    CourseUserDetailsDTO[]
  >([]);

  const isAdmin = useIsUserRole("admin");
  const { data: universities } = useGetUniversitiesQuery(undefined, {
    skip: !isAdmin,
  });
  const [selectedUniversityId, setSelectedUniversityId] = useState<
    string | null
  >(null);

  const [clickedCourse, setClickedCourse] = useState<CourseInfo>();

  const fuse = new Fuse(props.courses, {
    threshold: 0.4,
    keys: ["name", "lecturer.firstName", "lecturer.lastName"],
  });

  useEffect(() => {
    if (searchQuery === "") {
      setFilteredCourses(props.courses);
    } else {
      setFilteredCourses(fuse.search(searchQuery).map((result) => result.item));
    }
  }, [searchQuery, props.courses]);

  useModalFocusObserver(modalRef, inputRef);

  const [
    leaveCourse,
    { isError: leaveIsError, isSuccess: leaveSuccess, error: leaveError },
  ] = useLeaveCourseMutation();

  useToastEffect({
    isError: leaveIsError,
    isSuccess: leaveSuccess,
    error: leaveError,
    successMessage: t("coursePage.successLeave", {
      courseName: clickedCourse?.name,
      ns: "toast",
    }),
  });

  const handleLeaveCourse = async () => {
    await leaveCourse(clickedCourse?.id ?? "");
    closeModal("leave_modal");
    closeModal(SEARCH_MODAL_ID, () => setSearchQuery?.(""));
  };

  return (
    <Modal
      id={"search_modal"}
      title={t("searchCoursesTitle")}
      isWider
      onCancel={() => setSearchQuery("")}
      hideActionButtons={true}
      openOnMount={false}
      modalRef={modalRef}
    >
      <div className="flex h-[62vh] flex-col pt-2 pl-1">
        <input
          type="text"
          ref={inputRef}
          placeholder={t("searchbarPlaceholder")}
          className="w-full rounded-xl bg-neutral p-3 text-lg focus:outline-none"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {isAdmin && (
          <select
            className="select mt-md w-full max-w-xs"
            value={selectedUniversityId ?? ""}
            onChange={(e) => setSelectedUniversityId(e.target.value)}
          >
            <option key={"noUniversity"} value={""}>
              {t("filterAllUniversities")}
            </option>
            {universities?.map((uni) => (
              <option key={uni.id} value={uni.id}>
                {`${uni.fullName} (${uni.shortName})`}
              </option>
            ))}
          </select>
        )}
        <div className="divider" />
        <div className="overflow-x-hidden">
          <ul className="menu card z-10 mx-0 w-full rounded-box px-0">
            {filteredCourses
              .filter((course) =>
                selectedUniversityId
                  ? course.university?.id === selectedUniversityId
                  : true,
              )
              .map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  allCourses={true}
                  setCourseToEnroll={props.setCourseToEnroll}
                  setSearchQuery={setSearchQuery}
                  setClickedCourse={setClickedCourse}
                />
              ))}
          </ul>
        </div>
      </div>
      <Modal
        id="leave_modal"
        title={t("modalLeaveCourse", { ns: "coursePage" })}
        content={t("modalLeaveCourseContent", { ns: "coursePage" })}
        onConfirmLabel={t("modalLeaveCourseConfirmLabel", { ns: "coursePage" })}
        onConfirm={handleLeaveCourse}
        onCancelLabel={t("cancel", { ns: "commons" })}
        onCancel={() => {}}
      />
    </Modal>
  );
}

export default SearchCoursesModal;
